'use client';

import type { FinalPlan, PlanPeriod } from 'components';
import { isPlanDowngrade } from 'components';
import { JSX } from 'react';

import { Button } from '@videoblocks/design-system';

interface UpgradeCreditPlanCardButtonProps {
    selectedPlanCode: string | null;
    isCurrentPlan: boolean;
    isCancelledOrExpired: boolean;
    onUpgradePlan: (showConfirmationModal: boolean) => void;
    plan: FinalPlan;
    selectedPeriod: PlanPeriod;
    currentBillingAmount: number | null;
    currentAddonCode?: string;
}

export default function UpgradeCreditPlanCardButton({
    selectedPlanCode,
    isCurrentPlan,
    isCancelledOrExpired,
    onUpgradePlan,
    plan,
    selectedPeriod,
    currentBillingAmount,
    currentAddonCode,
}: UpgradeCreditPlanCardButtonProps): JSX.Element {
    const hasSelectedTier = Boolean(selectedPlanCode);
    const isUserCurrentPlan = isCurrentPlan;
    const isPlanDifferent = !isCurrentPlan;

    // Check if selected addon matches current addon
    const isCurrentAddonSelected = Boolean(
        selectedPlanCode && currentAddonCode && selectedPlanCode === currentAddonCode,
    );

    // Calculate if this would be a downgrade
    const isDowngrade = isPlanDowngrade(currentBillingAmount, plan, selectedPeriod, selectedPlanCode || undefined);

    let buttonLabel = 'Upgrade Plan';

    // Override logic for cancelled/expired subscriptions
    if (isCancelledOrExpired && isUserCurrentPlan && isCurrentAddonSelected) {
        buttonLabel = 'Restart Plan';
    } else if (isUserCurrentPlan && !hasSelectedTier) {
        buttonLabel = 'Current plan';
    } else if (isUserCurrentPlan && isCurrentAddonSelected) {
        buttonLabel = 'Current plan';
    } else if (isDowngrade) {
        buttonLabel = 'Downgrade Plan';
    }

    const isButtonDisabled =
        // Enable button for cancelled/expired subscriptions even if it's current plan
        isCancelledOrExpired && isUserCurrentPlan && hasSelectedTier
            ? false
            : (isUserCurrentPlan && !hasSelectedTier) ||
              (isUserCurrentPlan && isCurrentAddonSelected) ||
              (isPlanDifferent && !hasSelectedTier);

    const buttonClass = isButtonDisabled
        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
        : 'bg-blue-600 text-white hover:bg-blue-700';

    const handleClick = () => {
        onUpgradePlan(true);
    };

    return (
        <Button
            variant="alternate"
            size="large"
            className={`text-base min-h-[48px] ${buttonClass}`}
            disabled={isButtonDisabled}
            onClick={handleClick}
        >
            {buttonLabel}
        </Button>
    );
}
